import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader } from '@refera/ui-web'
import * as yup from 'yup'

import useStyles from './styles'
import { FormProvider, useForm } from 'react-hook-form'
import { BasicInput, Select } from '_/components/inputs'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { CRYPTO_TYPE, MIN_AZURE_SECRET_LENGTH } from './constants'
import { Button, Grid, Link, Typography } from '@mui/material'
import { createEmail, editEmail, getEmail, testEmail } from '_/modules/email/actions'
import * as emailService from '_/services/email'
import { emailSelector } from '_/modules/email/selectors'
import { Attachments } from '_/components/refera-ui'
import HeaderTitle from '_/components/header-title'
import { navigate, useParams } from '@reach/router'
import { yupResolver } from '@hookform/resolvers/yup'

const PROVIDER_KEYS = ['gmail', 'outlook', 'amazon_ses', 'sendgrid', 'refera']
const PROVIDER_MS_GRAPH = 'outlook'
const MASKED_SECRET = '********'
const NON_REQUIRED_SMTP_PORT_PROVIDER_OPTIONS = [PROVIDER_MS_GRAPH, 'refera']
const PROVIDER_OPTIONS = [
  { label: 'Gmail', value: 'gmail' },
  { label: 'Outlook Corporativo', value: PROVIDER_MS_GRAPH },
  { label: 'Amazon SES', value: 'amazon_ses' },
  { label: 'SendGrid', value: 'sendgrid' },
  { label: 'Refera', value: 'refera' },
]
const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
const STRICT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const SENSITIVE_KEYS = [
  'azureClientSecret',
  'password',
  'clientSecret',
  'apiKey',
  'token',
  'client_secret',
  'secret',
]

const REPLY_TO_EMAIL_HELP_TEXT =
  'Este endereço será utilizado como o e-mail de resposta padrão para as mensagens enviadas pela plataforma.'
const NO_SPECIAL_CHARACTERS_MESSAGE =
  'O subdomínio não pode conter espaçamentos ou caracteres especiais.'
const SUBDOMAIN_HELP_TEXT = () => (
  <span>
    O subdomínio informado será utilizado para compor o endereço de envio automático dos e-mails da
    plataforma.
    <br />
    <br />
    Os envios serão realizados pelo endereço:
    <br />
    {'noreply@<subdomínio>.refera.com.br'}
    <br />
    <br />
    Certifique-se de informar um subdomínio válido, pois ele impacta a identificação e a
    confiabilidade dos e-mails enviados.
  </span>
)

const safeLog = e => {
  try {
    const redactor = v => {
      if (!v || typeof v !== 'object') return v
      if (Array.isArray(v)) return v.map(redactor)
      const o = {}
      Object.keys(v).forEach(k => {
        const val = v[k]
        o[k] = SENSITIVE_KEYS.includes(k) ? '***' : redactor(val)
      })
      return o
    }
    const out = redactor(
      typeof e === 'object' ? e : { message: typeof e === 'string' ? e : String(e) }
    )
    console.error(out)
  } catch {
    console.error('Erro')
  }
}

const schema = yup.object({
  useMsgraph: yup.boolean().default(false),
  hasAzureSecret: yup.boolean().default(false),
  provider: yup
    .string()
    .transform((value, original) => (original === '' ? null : value))
    .oneOf([...PROVIDER_KEYS], 'Provedor inválido')
    .nullable(),
  username: yup
    .string()
    .trim()
    .test('username-required', 'Nome de Usuário é obrigatório', function usernameRequired(v) {
      const { provider } = this.parent
      return provider === 'refera' || !!v
    }),
  sendingDomain: yup.string().nullable(),
  // Campos exibidos caso o provedor seja Refera
  replyToEmail: yup
    .string()
    .trim()
    .transform((value, original) => (original === '' ? null : value))
    .nullable()
    .email('E-mail inválido')
    .test('reply-to-email-format', 'E-mail inválido', function replyEmailFormat(v) {
      if (!v) return true
      return STRICT_EMAIL_REGEX.test(v)
    })
    .test(
      'reply-to-email-required',
      'E-mail de Resposta é obrigatório',
      function replyToEmailRequired(v) {
        const { provider } = this.parent
        return provider !== 'refera' || !!v
      }
    ),
  subdomain: yup
    .string()
    .trim()
    .transform((value, original) => (original === '' ? null : value))
    .nullable()
    .test('subdomain-format', 'Subdomínio inválido', function subdomainFormat(v) {
      if (!v) return true
      if (!/^[a-z0-9-]+$/.test(v)) {
        return this.createError({ message: NO_SPECIAL_CHARACTERS_MESSAGE })
      }
      if (v.startsWith('-') || v.endsWith('-')) return false
      if (v.length < 1 || v.length > 63) return false
      return true
    })
    .test('subdomain-required', 'Subdomínio é obrigatório', function subdomainRequired(v) {
      const { provider } = this.parent
      return provider !== 'refera' || !!v
    }),
  // Campo opcional para salvar; validação de presença será feita apenas ao enviar teste
  testEmailRecipient: yup
    .string()
    .trim()
    .transform((value, original) => (original === '' ? null : value))
    .nullable()
    .email('E-mail inválido')
    .test('test-email-format', 'E-mail inválido', function testEmailFormat(v) {
      if (!v) return true
      return STRICT_EMAIL_REGEX.test(v)
    }),
  smtpAddress: yup
    .string()
    .trim()
    .nullable()
    .test('smtp-address-required', 'Endereço SMTP é obrigatório', function smtpAddressRequired(v) {
      const { useMsgraph, provider } = this.parent
      const msGraphMode = useMsgraph || provider === PROVIDER_MS_GRAPH || provider === 'refera'
      return msGraphMode ? true : !!v
    }),
  smtpPort: yup
    .mixed()
    .nullable()
    .test(
      'smtp-port-conditional',
      'Porta SMTP deve ser um número',
      function smtpPortConditional(v) {
        const { useMsgraph, provider } = this.parent
        const msGraphMode = useMsgraph || provider === PROVIDER_MS_GRAPH || provider === 'refera'
        if (msGraphMode) return true
        if (v === undefined || v === null || v === '') {
          return this.createError({ message: 'Porta SMTP é obrigatória' })
        }
        const num = Number(v)
        if (Number.isNaN(num)) return false
        if (!Number.isInteger(num)) {
          return this.createError({ message: 'Porta SMTP deve ser um número inteiro' })
        }
        if (num < 1 || num > 65535) {
          return this.createError({ message: 'Porta SMTP inválida' })
        }
        return true
      }
    ),
  encryptionMethod: yup
    .string()
    .nullable()
    .test(
      'encryption-required',
      'Método de Criptografia é obrigatório',
      function encryptionRequired(v) {
        const { useMsgraph, provider } = this.parent
        const msGraphMode = useMsgraph || provider === PROVIDER_MS_GRAPH || provider === 'refera'
        return msGraphMode ? true : !!v
      }
    ),
  password: yup
    .string()
    .trim()
    .nullable()
    .test('password-required', 'Senha/API Key é obrigatória', function passwordRequired(v) {
      const { useMsgraph, provider } = this.parent
      const msGraphMode = useMsgraph || provider === PROVIDER_MS_GRAPH || provider === 'refera'
      return msGraphMode ? true : !!v
    }),
  // Azure fields required when using MS Graph
  azureTenantId: yup
    .string()
    .trim()
    .nullable()
    .test('tenant-required', 'Tenant ID é obrigatório', function tenantRequired(v) {
      const { provider } = this.parent
      const msGraphMode = provider === PROVIDER_MS_GRAPH
      if (!msGraphMode) return true
      if (!v) return false
      return UUID_REGEX.test(v) || this.createError({ message: 'Tenant ID inválido' })
    }),
  azureClientId: yup
    .string()
    .trim()
    .nullable()
    .test('client-id-required', 'Client ID é obrigatório', function clientIdRequired(v) {
      const { provider } = this.parent
      const msGraphMode = provider === PROVIDER_MS_GRAPH
      if (!msGraphMode) return true
      if (!v) return false
      return UUID_REGEX.test(v) || this.createError({ message: 'Client ID inválido' })
    }),
  azureClientSecret: yup
    .string()
    .trim()
    .nullable()
    .test('azure-secret-required', 'Segredo é obrigatório', function azureSecretRequired(value) {
      const { provider, hasAzureSecret } = this.parent
      const msGraphMode = provider === PROVIDER_MS_GRAPH
      if (!msGraphMode) return true
      if (hasAzureSecret && !value) return true
      if (hasAzureSecret && value === MASKED_SECRET) return true
      return !!value && value.length >= MIN_AZURE_SECRET_LENGTH
    }),
})

export default function AdditionalConfigEmail() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { agencyId } = useParams()
  const email = useSelector(emailSelector)
  const authToken = useSelector(state => state.user.authToken)
  const [loading, setLoading] = useState(false)
  const [attachmentsBaseboard, setAttachmentsBaseboard] = useState()
  const [attachmentsHeader, setAttachmentsHeader] = useState()
  const hasAzureSecret = useMemo(
    () => email?.hasAzureClientSecret || email?.azureClientSecretConfigured || false,
    [email]
  )

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      useMsgraph: email?.useMsgraph || false,
      hasAzureSecret,
      provider: email?.useMsgraph ? PROVIDER_MS_GRAPH : 'gmail',
      smtpAddress: email?.smtpAddress,
      smtpPort: email?.smtpPort,

      encryptionMethod: email?.encryptionMethod,
      username: email?.username,
      password: email?.password,
      sendingDomain: email?.sendingDomain,
      azureTenantId: email?.azureTenantId,
      azureClientId: email?.azureClientId,
      azureClientSecret: '',
    },
  })

  const { handleSubmit, errors, reset, setValue, watch, formState, clearErrors, register } = methods
  const providerSelected = watch('provider')
  const isAzureSecretConfigured = watch('hasAzureSecret')

  const hideSmtpPortFields = NON_REQUIRED_SMTP_PORT_PROVIDER_OPTIONS.includes(providerSelected)

  useEffect(() => {
    register('useMsgraph')
    register('hasAzureSecret')
  }, [register])

  const onFormError = useCallback(
    errs => {
      const messages = Object.values(errs || {})
        .map(e => e?.message)
        .filter(Boolean)
      showToast({ type: 'error', message: messages?.[0] || 'Preencha os campos obrigatórios.' })
    },
    [showToast]
  )

  useEffect(() => {
    const isMsGraph = providerSelected === PROVIDER_MS_GRAPH
    setValue('useMsgraph', isMsGraph, {
      shouldDirty: true,
      shouldValidate: true,
    })

    if (isMsGraph) {
      clearErrors(['smtpAddress', 'smtpPort', 'encryptionMethod', 'password'])

      if (isAzureSecretConfigured) {
        setValue('azureClientSecret', MASKED_SECRET, { shouldDirty: false, shouldValidate: false })
      }
    } else {
      clearErrors(['azureTenantId', 'azureClientId', 'azureClientSecret'])
      setValue('azureTenantId', '', { shouldDirty: true, shouldValidate: false })
      setValue('azureClientId', '', { shouldDirty: true, shouldValidate: false })
      setValue('azureClientSecret', '', { shouldDirty: true, shouldValidate: false })
    }
  }, [providerSelected, setValue, clearErrors, isAzureSecretConfigured])

  useEffect(() => {
    if (providerSelected === PROVIDER_MS_GRAPH) {
      if (isAzureSecretConfigured) {
        setValue('azureClientSecret', MASKED_SECRET, {
          shouldDirty: false,
          shouldValidate: false,
        })
      } else {
        setValue('azureClientSecret', '', { shouldDirty: true, shouldValidate: false })
      }
    }
  }, [providerSelected, isAzureSecretConfigured, setValue])

  const onSubmit = useCallback(
    async data => {
      if (!data?.provider) {
        showToast({ type: 'error', message: 'Selecione um provedor de email.' })
        return
      }

      setLoading(true)
      const isOutlook = data?.provider === PROVIDER_MS_GRAPH

      let payload = {}

      if (isOutlook) {
        payload = {
          useMsgraph: true,
          provider: PROVIDER_MS_GRAPH,
          username: data.username,
          azureTenantId: data.azureTenantId,
          azureClientId: data.azureClientId,
          header: attachmentsHeader?.[0],
          baseboard: attachmentsBaseboard?.[0],
        }

        if (data.azureClientSecret && data.azureClientSecret !== MASKED_SECRET) {
          payload.azureClientSecret = data.azureClientSecret
        }
      } else {
        payload = {
          provider: data?.provider?.toLowerCase(),
          username: data.username,
          smtpAddress: data.smtpAddress,
          smtpPort: data.smtpPort,
          encryptionMethod: data.encryptionMethod,
          password: data.password,
          sendingDomain: data.sendingDomain,
          header: attachmentsHeader?.[0],
          baseboard: attachmentsBaseboard?.[0],
          ...(data?.provider === 'refera' && {
            replyToEmail: data.replyToEmail,
            subdomain: data.subdomain,
          }),
        }
      }

      dispatch(email?.id ? editEmail(agencyId, payload) : createEmail(agencyId, payload))
        .then(() => {
          showToast({ type: 'success', message: 'Configuração de email salva com sucesso' })
        })
        .catch(error => {
          safeLog(error)
          showToast({
            type: 'error',
            message: error?.message || 'Erro ao salvar configuração de email',
          })
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [attachmentsHeader, attachmentsBaseboard, dispatch, showToast, email, agencyId]
  )

  const onTestSubmit = useCallback(
    async data => {
      setLoading(true)
      const msGraphMode = data?.useMsgraph || data?.provider === PROVIDER_MS_GRAPH
      const payload = msGraphMode
        ? {
            useMsgraph: true,
            username: data?.username,
            azureTenantId: data?.azureTenantId,
            azureClientId: data?.azureClientId,
            ...(data?.azureClientSecret &&
              data?.azureClientSecret !== MASKED_SECRET && {
                azureClientSecret: data?.azureClientSecret,
              }),
            provider: PROVIDER_MS_GRAPH,
          }
        : {
            useMsgraph: false,
            username: data?.username,
            smtpAddress: data?.smtpAddress,
            smtpPort: data?.smtpPort,
            encryptionMethod: data?.encryptionMethod,
            password: data?.password,
          }

      dispatch(testEmail(payload))
        .then(() => {
          showToast({ type: 'success' })
        })
        .catch(error => {
          safeLog(error)
          showToast({ type: 'error', message: 'Erro de conexão. Revise seus dados.' })
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [dispatch, showToast]
  )

  const onSendTestEmail = useCallback(
    async data => {
      setLoading(true)
      if (!data?.testEmailRecipient) {
        showToast({ type: 'error', message: 'Informe o E-mail de teste para enviar.' })
        setLoading(false)
        return
      }
      const payload = { testEmail: data?.testEmailRecipient }
      emailService
        .sendAgencyTestEmailService(authToken)(agencyId, payload)
        .then(() => {
          showToast({ type: 'success', message: 'E-mail de teste enviado.' })
        })
        .catch(error => {
          safeLog(error)
          let message = error?.error_message || error?.message
          if (!message && error && typeof error === 'object') {
            try {
              const values = Object.values(error)
              const fieldMsg = values.find(v => Array.isArray(v) && typeof v?.[0] === 'string')
              message = fieldMsg?.[0]
            } catch (e) {
              // fallback in case of parsing error
            }
          }
          showToast({ type: 'error', message: message || 'Falha ao enviar e-mail de teste' })
        })
        .finally(() => setLoading(false))
    },
    [authToken, agencyId, showToast]
  )

  const getDataForm = useCallback(() => {
    let isMounted = true
    if (agencyId) {
      setLoading(true)

      dispatch(getEmail(agencyId))
        .then(data => {
          if (!isMounted) return

          setValue('useMsgraph', data?.useMsgraph || false)
          setValue(
            'hasAzureSecret',
            data?.hasAzureClientSecret || data?.azureClientSecretConfigured || false
          )

          let defaultProvider = data?.provider || 'gmail'
          if (data?.useMsgraph) defaultProvider = PROVIDER_MS_GRAPH
          setValue('provider', defaultProvider)

          setValue('smtpAddress', data?.smtpAddress)
          setValue('smtpPort', data?.smtpPort)
          setValue('encryptionMethod', data?.encryptionMethod)
          setValue('username', data?.username)
          setValue('password', data?.password)
          setValue('sendingDomain', data?.sendingDomain)
          setValue('azureTenantId', data?.azureTenantId)
          setValue('azureClientId', data?.azureClientId)
          const secretConfigured =
            data?.hasAzureClientSecret || data?.azureClientSecretConfigured || false
          if (data?.useMsgraph && secretConfigured) {
            setValue('azureClientSecret', MASKED_SECRET)
          } else {
            setValue('azureClientSecret', '')
          }
          if (data?.header) {
            setAttachmentsHeader([
              {
                file: data?.header,
                id: 1,
              },
            ])
          }

          if (data?.baseboard) {
            setAttachmentsBaseboard([
              {
                file: data?.baseboard,
                id: 2,
              },
            ])
          }
          setLoading(false)
        })
        .catch(() => {
          if (isMounted) {
            showToast({ type: 'error', message: 'Ocorreu um erro ao buscar os dados.' })
            setLoading(false)
          }
        })
    }
    return () => {
      isMounted = false
    }
  }, [dispatch, agencyId, setValue, showToast, setLoading])

  useEffect(() => {
    const cleanup = getDataForm()
    return () => {
      if (typeof cleanup === 'function') cleanup()
    }
  }, [agencyId, getDataForm])

  function handleReset() {
    if (agencyId) {
      getDataForm()
    }

    showToast({ type: 'success', message: 'Modificações canceladas' })
    return reset()
  }

  const goBack = () => {
    navigate(-1)
  }

  return (
    <Grid className={styles.app}>
      <HeaderTitle title="Provedor de email" backButtonAction={goBack}>
        <Grid style={{ display: 'flex', gap: '8px' }}>
          <Button
            className={[styles.buttonBlue, styles.button]}
            form="formEmail"
            type="button"
            onClick={handleSubmit(onTestSubmit)}
            disabled={loading}
          >
            Testar conexão de email
          </Button>
          {providerSelected === PROVIDER_MS_GRAPH && (
            <Button
              className={[styles.buttonBlue, styles.button]}
              form="formEmail"
              type="button"
              onClick={handleSubmit(onSendTestEmail)}
              disabled={loading}
            >
              Enviar email de teste
            </Button>
          )}
          <Button className={[styles.buttonRed, styles.button]} onClick={() => handleReset()}>
            Cancelar
          </Button>
          <Button
            className={[styles.buttonFullBlue, styles.button]}
            form="formEmail"
            type="button"
            onClick={handleSubmit(onSubmit, onFormError)}
            disabled={loading || !formState?.isDirty}
          >
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <Loader hasBackdrop open={loading} />
      <FormProvider {...methods}>
        <form className={styles.container} id="formEmail">
          <Typography className={styles.info}>
            Configure as informações deste grupo para que os emails sejam enviados aos seus clientes
            sobre atualizações de chamados.{' '}
            <Link
              href="https://refera.movidesk.com/kb/pt-br/article/531751/configuracao-de-e-mail"
              target="_blank"
            >
              Clique aqui
            </Link>{' '}
            para entender como fazer essa configuração.
          </Typography>
          <Grid className={styles.column}>
            <Select
              label="Provedor de Serviço de Email"
              name="provider"
              defaultValue={providerSelected}
              options={PROVIDER_OPTIONS}
              getLabel={item => item.label}
              getValue={item => item.value}
              getKey={item => item.value}
              menuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                transformOrigin: { vertical: 'top', horizontal: 'center' },
              }}
              containerClasses={styles.select}
            />
          </Grid>

          {providerSelected === 'refera' && (
            <Grid className={styles.line}>
              <Grid className={styles.column}>
                <BasicInput
                  label="E-mail de Resposta (Reply-To)"
                  name="replyToEmail"
                  defaultValue={email?.replyToEmail}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                  }}
                  helpText={REPLY_TO_EMAIL_HELP_TEXT}
                />
              </Grid>
              <Grid className={styles.column}>
                <BasicInput
                  label="Subdomínio"
                  name="subdomain"
                  defaultValue={email?.subdomain}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 100,
                      message: `Esse campo aceita no máximo 100 caracteres`,
                    },
                  }}
                  helpText={SUBDOMAIN_HELP_TEXT()}
                />
              </Grid>
            </Grid>
          )}

          {!hideSmtpPortFields && (
            <Grid className={styles.column}>
              <BasicInput
                label="Endereço SMTP"
                name="smtpAddress"
                defaultValue={email?.smtpAddress}
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 255,
                    message: `Esse campo aceita no máximo 255 caracteres`,
                  },
                }}
              />
            </Grid>
          )}

          {!hideSmtpPortFields && (
            <Grid className={styles.column}>
              <BasicInput
                label="Porta SMTP"
                name="smtpPort"
                defaultValue={email?.smtpPort}
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 255,
                    message: `Esse campo aceita no máximo 255 caracteres`,
                  },
                }}
              />
            </Grid>
          )}
          <Grid className={styles.line}>
            {!hideSmtpPortFields && (
              <Grid className={styles.column}>
                <Select
                  label="Método de Criptografia"
                  name="encryptionMethod"
                  defaultValue={email?.encryptionMethod}
                  error={!!errors?.encryptionMethod}
                  options={CRYPTO_TYPE}
                  getLabel={item => item.label}
                  getValue={item => item.value}
                  getKey={item => item.value}
                  menuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  }}
                  style={{ gridArea: 'businessFront' }}
                  containerClasses={styles.select}
                />
              </Grid>
            )}
            {providerSelected !== 'refera' && (
              <Grid className={styles.column}>
                <BasicInput
                  label="Nome de Usuário (Login)"
                  name="username"
                  defaultValue={email?.username}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>

          {!hideSmtpPortFields && (
            <Grid className={styles.line}>
              <Grid className={styles.column}>
                <BasicInput
                  label="Senha/API Key"
                  name="password"
                  defaultValue={email?.password}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                  }}
                  type="password"
                />
              </Grid>
              <Grid className={styles.column}>
                <BasicInput
                  label="Domínio de Envio"
                  name="sendingDomain"
                  defaultValue={email?.sendingDomain}
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: `Esse campo aceita no máximo 255 caracteres`,
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}

          {!hideSmtpPortFields && (
            <>
              <Grid className={styles.line}>
                <Grid className={styles.column}>
                  <BasicInput
                    label="Azure Tenant ID"
                    name="azureTenantId"
                    defaultValue={email?.azureTenantId}
                    labelClasses={styles.label}
                    rules={{
                      maxLength: {
                        value: 255,
                        message: `Esse campo aceita no máximo 255 caracteres`,
                      },
                    }}
                  />
                </Grid>
                <Grid className={styles.column}>
                  <BasicInput
                    label="Azure Client ID"
                    name="azureClientId"
                    defaultValue={email?.azureClientId}
                    labelClasses={styles.label}
                    rules={{
                      maxLength: {
                        value: 255,
                        message: `Esse campo aceita no máximo 255 caracteres`,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid className={styles.column}>
                <BasicInput
                  label={`Azure Client Secret${isAzureSecretConfigured ? ' (configurado)' : ''}`}
                  name="azureClientSecret"
                  defaultValue={isAzureSecretConfigured ? MASKED_SECRET : ''}
                  labelClasses={styles.label}
                  type="password"
                  placeholder={
                    isAzureSecretConfigured ? 'Segredo configurado' : 'Informe para configurar'
                  }
                  disabled={!hideSmtpPortFields}
                  showLabelSpan={providerSelected === PROVIDER_MS_GRAPH && !isAzureSecretConfigured}
                />
              </Grid>
              <Grid className={styles.column}>
                <BasicInput
                  label="E-mail de teste"
                  name="testEmailRecipient"
                  defaultValue=""
                  labelClasses={styles.label}
                />
              </Grid>
            </>
          )}

          <Grid className={styles.column}>
            <Attachments
              label="Cabeçalho padrão"
              files={attachmentsHeader || []}
              accept={{
                'image/jpeg': ['.jpeg', '.png', '.jpg', '.bmp', '.flv'],
              }}
              multiple={false}
              onDrop={files => setAttachmentsHeader(files)}
              onRemoveItem={() => setAttachmentsHeader()}
              onItemClick={index => {
                window.open(attachmentsHeader[index]?.file)
              }}
            />
          </Grid>
          <Grid className={styles.column}>
            <Attachments
              label="Rodapé padrão"
              files={attachmentsBaseboard || []}
              accept={{
                'image/jpeg': ['.jpeg', '.png', '.jpg', '.bmp', '.flv'],
              }}
              multiple={false}
              onDrop={files => setAttachmentsBaseboard(files)}
              onRemoveItem={() => setAttachmentsBaseboard([])}
              onItemClick={index => {
                window.open(attachmentsBaseboard[index]?.file)
              }}
            />
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  )
}
